import React, { useCallback, useState, useMemo, useEffect, useRef } from 'react';
import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  Typography,
  withStyles,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckBoxController } from '../../../components/organisms/form/controllers/CheckBoxController';
import { FormProvider, useFieldArray, useForm, useFormState } from 'react-hook-form';
import { TextFieldController } from '../../../components/organisms/form/controllers/TextFieldController';
import { KeyboardDatePickerController } from '../../../components/organisms/form/controllers/KeyboardDatePickerController';
import { KeyboardTimePickerController } from '../../../components/organisms/form/controllers/KeyboardTimePickerController';
import { DeleteForeverOutlined } from '@material-ui/icons';
import { CouponPackMasterImageBody } from './Image';
import { TextAreaController } from '../../../components/organisms/form/controllers/TextAreaController';
import { BasicTableHead, BasicTableHeadProps } from '../../../components/molecules';
import { theme } from '../../../theme';
import { CouponPackMasterFormInput, CouponPackMasterFormProps, ImageProps } from '../../../types/couponPackMaster';
import {
  CouponConsumerDisplayPreviewProps,
  CouponPackMasterSmartCheckDisplayPreview,
} from './CouponPackMasterSmartCheckDisplayPreview';
import { TableCellInputNumberOfUses } from './components/TableCellInputNumberOfUses';
import { SmartCheckImageUrl, SmartCheckSetting } from '../../../graphql/generated';

const useStyles = makeStyles((theme: Theme) => ({
  section: {
    border: '1px solid #c9cace',
    borderRadius: theme.spacing(1),
    padding: theme.spacing(3),
    marginTop: theme.spacing(4),
  },
  tableContainer: {
    maxHeight: '710px',
    overflowY: 'auto',
    '& .MuiTableCell-root': {
      borderLeft: '1px solid rgba(224, 224, 224, 1)',
      padding: '8px 16px 8px 16px',
    },
  },
  emptyBox: {
    marginTop: theme.spacing(4),
  },
  controlPanel: {
    padding: `${theme.spacing(1)}px 0`,
    position: 'sticky',
    bottom: '0',
    zIndex: 2,
    backgroundColor: 'white',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: theme.palette.grey['900'],
  },
  disabledButton: {
    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'all !important',
    },
  },
}));

function getImageUrl(key: keyof ImageProps, props: CouponPackMasterFormProps): string | undefined {
  if (props[key].selectedFile && props[key].selectedFile.deleteFlag) {
    return undefined;
  } else if (props[key].selectedFile.file) {
    return URL.createObjectURL(props[key].selectedFile.file);
  } else if (props[key].currentImageUrl) {
    return props[key].currentImageUrl;
  } else {
    return undefined;
  }
}

const WhiteTextTypography = withStyles({
  root: {
    color: '#FFFFFF',
  },
})(Typography);

const tableHeadProps: BasicTableHeadProps = {
  columns: [
    {
      name: 'couponCode',
      label: 'クーポンコード',
      minWidth: theme.spacing(30),
      align: 'left',
    },
    {
      name: 'couponMasterName',
      label: 'クーポン名',
      minWidth: theme.spacing(30),
      align: 'left',
    },
    {
      name: 'quantity',
      label: '利用回数',
      minWidth: theme.spacing(30),
      width: theme.spacing(40),
      align: 'left',
    },
    {
      name: 'maxCouponsPerOrder',
      label: '1回の利用上限数',
      minWidth: theme.spacing(30),
      width: theme.spacing(40),
      align: 'left',
    },
    {
      name: 'action',
      label: '',
      minWidth: theme.spacing(5),
      align: 'left',
    },
  ],
};

export const CouponPackMasterForm: React.FC<CouponPackMasterFormProps> = (props) => {
  const styles = useStyles();
  const useCouponPackMasterForm = useForm<CouponPackMasterFormInput>({
    defaultValues: props.previousValues,
    mode: 'onBlur',
  });
  const { handleSubmit, setValue, control, getValues, clearErrors, trigger, watch } = useCouponPackMasterForm;
  const { errors } = useFormState({ control });
  const [inPreview, setInPreview] = useState(false);
  const [previewInput, setPreviewInput] = useState<CouponPackMasterFormInput>(getValues() as CouponPackMasterFormInput);
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  // Infinite scroll state
  const CHUNK_SIZE = 10;
  const [visibleCount, setVisibleCount] = useState(CHUNK_SIZE);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isAddingCoupon, setIsAddingCoupon] = useState(false);
  const [smartCheckSettings, setSmartCheckSettings] = useState<{
    [key: string]: {
      smartCheckImageUrl?: SmartCheckImageUrl;
      smartCheckSetting?: SmartCheckSetting;
    };
  }>({});
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const visibleFields = useMemo(() => {
    return fields.slice(0, visibleCount);
  }, [fields, visibleCount]);

  // Memoize isDisabled flag
  const isDisabled = props.previousValues.status === 'ISSUED';

  // Memoize remove callback
  const handleRemove = useCallback(
    (index: number) => {
      remove(index);
    },
    [remove]
  );

  // PREVIEW LOGIC
  const onPreview = useCallback(async () => {
    clearErrors();
    const result = await trigger();

    if (!result) {
      return;
    }

    setPreviewInput({
      ...getValues(),
      products: getValues('products').map((product, index) => ({
        ...product,
        smartCheckImageUrl:
          props.previousValues.products[index]?.smartCheckImageUrl ||
          smartCheckSettings[product.id]?.smartCheckImageUrl,
        smartCheckSetting:
          props.previousValues.products[index]?.smartCheckSetting || smartCheckSettings[product.id]?.smartCheckSetting,
      })),
    } as CouponPackMasterFormInput);
    setInPreview(true);
  }, [clearErrors, trigger, getValues, props.previousValues.products, smartCheckSettings]);

  const preViewProps: CouponConsumerDisplayPreviewProps = {
    open: inPreview,
    setOpen: setInPreview,
    input: previewInput,

    // ファイルがセットされていればそのURLを、既存の値があればそれを、それもなければ undefined を返す
    logoImageUrl: getImageUrl('logoImageProps', props),
    couponImageUrl: getImageUrl('couponImageProps', props),
  };

  const handleLoadCouponMaster = (event: React.SyntheticEvent) => {
    const tableNode = event.currentTarget as HTMLDivElement;
    if (tableNode.scrollTop + tableNode.clientHeight >= tableNode.scrollHeight - 0) {
      if (visibleCount < fields.length && !isLoadingMore) {
        setIsLoadingMore(true);
        setTimeout(() => {
          const nextCount = Math.min(visibleCount + CHUNK_SIZE, fields.length);
          setVisibleCount(nextCount);
          setIsLoadingMore(false);
        }, 300);
      }
    }
  };

  const handleAddMoreCouponMaster = () => {
    setIsAddingCoupon(true);
    append({ couponMasterName: '', quantity: '', couponMasterCode: '', maxCouponsPerOrder: '' });
    // Load all remaining items - use callback to ensure we get the latest count
    setVisibleCount((prevCount) => Math.max(prevCount, fields.length) + 1);
    setTimeout(() => {
      if (tableContainerRef.current) {
        const { scrollHeight, clientHeight } = tableContainerRef.current;
        if (scrollHeight > clientHeight) {
          // Table has scroll, scroll within table
          tableContainerRef.current.scrollTo({
            top: scrollHeight,
            behavior: 'smooth',
          });
        } else {
          // Table has no scroll, scroll window
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          });
        }
      }
      setIsAddingCoupon(false);
    }, 300);
  };

  return (
    <FormProvider {...useCouponPackMasterForm}>
      <form autoComplete="off" noValidate onSubmit={handleSubmit(props.onSubmit)}>
        <Grid container spacing={2} className={styles.section}>
          <Grid item xs={12}>
            <Typography variant="h4">回数券利用設定</Typography>
          </Grid>
          <Grid item xs={12}>
            <CheckBoxController name="unAvailable" label={'利用不可'} />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={styles.section}>
          <Grid item xs={12}>
            <Typography variant="h4">基本情報</Typography>
          </Grid>
          <Grid container item alignItems="center">
            <Grid item xs={3}>
              <Typography variant="subtitle1">回数券名</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextFieldController
                name="couponName"
                size="small"
                label="100文字以内"
                required
                rule={{
                  required: '回数券名は必須です',
                  maxLength: {
                    value: 100,
                    message: '回数券名は100文字以内です',
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid container item alignItems="center">
            <Grid item xs={3}>
              <Typography variant="subtitle1">受取画面回数券名</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextFieldController
                name="displayCouponName"
                size="small"
                label="200文字以内"
                rule={{
                  maxLength: {
                    value: 200,
                    message: '受取画面回数券名は200文字以内です',
                  },
                }}
              />
            </Grid>
          </Grid>
          <Grid container item alignItems="center">
            <Grid item xs={3}>
              <Typography variant="subtitle1">回数券コード</Typography>
            </Grid>
            <Grid item xs={9}>
              <TextFieldController
                name="couponCode"
                size="small"
                label="回数券コード（100文字以内）"
                required
                rule={{
                  required: '回数券コードは必須です',
                  pattern: {
                    value: /^([a-zA-Z0-9-]{0,100})$/,
                    message: '回数券コードは100文字以内の半角英数字です',
                  },
                }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} alignItems="center" className={styles.section}>
          <Grid item xs={12}>
            <Typography variant="h4">日付・時刻設定</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">表示期間</Typography>
          </Grid>
          <Grid item xs={9}>
            <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={6}>
                <KeyboardDatePickerController required name="displayStartDate" label="表示開始日" />
              </Grid>
              <Grid item xs={6}>
                <KeyboardDatePickerController required name="displayEndDate" label="表示終了日" />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">有効期間</Typography>
          </Grid>
          <Grid item xs={9}>
            <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start">
              <Grid item xs={12}>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={1}>
                  <Grid item xs={6}>
                    <KeyboardDatePickerController required={false} name="validityStartDate" label="有効開始日" />
                  </Grid>
                  <Grid item xs={4}>
                    <KeyboardTimePickerController required={false} name="validityStartTime" label="時刻" />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color={'secondary'}
                      onClick={() => {
                        setValue('validityStartDate', null);
                        setValue('validityStartTime', null);
                      }}
                    >
                      <DeleteForeverOutlined />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" spacing={1}>
                  <Grid item xs={6}>
                    <KeyboardDatePickerController required name="validityEndDate" label="有効終了日" />
                  </Grid>
                  <Grid item xs={4}>
                    <KeyboardTimePickerController required name="validityEndTime" label="時刻" />
                  </Grid>
                  <Grid item xs={2}>
                    <IconButton
                      color={'secondary'}
                      onClick={() => {
                        setValue('validityEndDate', null);
                        setValue('validityEndTime', null);
                      }}
                    >
                      <DeleteForeverOutlined />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid container spacing={2} className={styles.section}>
          <Grid item xs={12}>
            <Typography variant="h4">回数券画像</Typography>
          </Grid>
          <Grid item xs={4}>
            <CouponPackMasterImageBody {...props.logoImageProps} fileTypeText="ロゴ画像" />
          </Grid>
          <Grid item xs={4}>
            <CouponPackMasterImageBody {...props.couponImageProps} fileTypeText="回数券画像" />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={styles.section}>
          <Grid item xs={12}>
            <Typography variant="h4">説明・注意事項</Typography>
          </Grid>
          <Grid item xs={12}>
            <TextAreaController name={'description'} maxLength={2000} label={'回数券説明文'} />
          </Grid>
          <Grid item xs={12}>
            <TextAreaController name={'note'} maxLength={2000} label={'お問い合わせ先'} />
          </Grid>
        </Grid>

        <Grid container spacing={2} className={styles.section}>
          <Grid item xs={12}>
            <Typography variant="h4">クーポン・利用回数</Typography>
          </Grid>
          <Grid item xs={12}>
            <TableContainer
              component={Paper}
              className={styles.tableContainer}
              onScroll={handleLoadCouponMaster}
              ref={tableContainerRef}
            >
              <Table stickyHeader>
                <BasicTableHead columns={tableHeadProps.columns} />
                <TableBody>
                  {visibleFields.map((field, index) => (
                    <TableRow key={field.id}>
                      <TableCellInputNumberOfUses
                        control={control}
                        watch={watch}
                        index={index}
                        fieldId={field.id}
                        errors={errors}
                        setValue={setValue}
                        fieldsLength={fields.length}
                        onRemove={handleRemove}
                        previousValues={props.previousValues}
                        trigger={trigger}
                        isDisabled={isDisabled}
                        setSmartCheckSettings={setSmartCheckSettings}
                      />
                    </TableRow>
                  ))}
                  {(isLoadingMore || isAddingCoupon) && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" style={{ padding: '16px' }}>
                        読み込み中…
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button
                type="button"
                size="medium"
                color="default"
                onClick={handleAddMoreCouponMaster}
                variant="contained"
                disabled={isDisabled || fields.length >= 100 || isAddingCoupon}
                className={styles.disabledButton}
                endIcon={isAddingCoupon ? <CircularProgress size={20} color="inherit" /> : undefined}
              >
                行を追加する
              </Button>
            </Box>
          </Grid>
        </Grid>

        <Box className={styles.emptyBox} />
        <Grid container spacing={2} justifyContent="flex-end" alignItems="center" className={styles.controlPanel}>
          <Divider />
          <Grid item xs={3}>
            <Button
              fullWidth
              variant="outlined"
              color="primary"
              disabled={props.loading}
              onClick={async () => await onPreview()}
            >
              プレビュー
            </Button>
          </Grid>
          <Grid item xs={3}>
            <Button fullWidth variant="contained" color="secondary" type="submit">
              {props.isUpdate ? '更新する' : '作成する'}
            </Button>
          </Grid>
        </Grid>
        <Backdrop className={styles.backdrop} open={props.loading}>
          <Grid container spacing={2} direction="column" justifyContent="center" alignItems="center">
            <Grid item xs={12}>
              <CircularProgress color="inherit" />
            </Grid>
            <Grid item xs={12}>
              <WhiteTextTypography variant="h2">{props.submitStepMessage}</WhiteTextTypography>
            </Grid>
          </Grid>
        </Backdrop>
      </form>

      {inPreview && <CouponPackMasterSmartCheckDisplayPreview {...preViewProps} />}
    </FormProvider>
  );
};
